import React, { FC } from 'react';
import styled from 'styled-components';
import { stringFormat, T } from 'src/shared/helpers';
import { DropdownActionItem, DropdownActions } from '../../../../../widgets/dropdown';
import useComposerStore from '../../configurator-composer/composer/composerStore';
import { requestComposerReveal } from './composerReveal';
import { ComposerAttributeNodeLink, ComposerAttributeType, getComposerLinkOptionIDs } from './attributes/composerAttributesModels';

// «USED BY N» (prototipo, utente 2026-10-20): quante condizioni dei Links dipendono da questo attributo o da questa
// opzione. Il dato non è nuovo: si legge dai links di tutti i nodi. Il menu porta alla condizione — sceglie chi la
// porta e apre i suoi Links —, così prima di cambiare o togliere qualcosa si vede chi ne resterebbe senza.

type User = { id: string; name: string; kind: 'attribute' | 'option'; ownerID: string };

const refersTo = (links: ComposerAttributeNodeLink[] | undefined, subjectID: string, kind: 'attribute' | 'option') =>
    (links ?? []).some((link) => (kind === 'attribute' ? link.attributeID === subjectID : getComposerLinkOptionIDs(link).includes(subjectID)));

const Chip = styled.button`
    display: inline-flex;
    height: 16px;
    align-items: center;
    padding: 0 6px;
    border: 0;
    border-radius: 999px;
    background: var(--editor-hover, #eceef0);
    color: var(--editor-muted, #55595f);
    font: inherit;
    font-size: 10px;
    cursor: pointer;

    &:hover {
        color: var(--editor-fg, #262a30);
    }
`;

export const ComposerUsedBy: FC<{ subjectID: string; kind: 'attribute' | 'option' }> = ({ subjectID, kind }) => {
    const [attributes, selectAttributes, selectOptions, setAttributeFace, setOptionFace] = useComposerStore((store) => [
        store.attributes,
        store.selectAttributes,
        store.selectOptions,
        store.setAttributeFace,
        store.setOptionFace
    ]);
    const users: User[] = [
        ...attributes
            .filter((attribute) => attribute.id !== subjectID && refersTo(attribute.links, subjectID, kind))
            .map((attribute) => ({ id: attribute.id, name: attribute.name, kind: 'attribute' as const, ownerID: attribute.id })),
        ...attributes
            .filter((attribute) => attribute.type === ComposerAttributeType.Attribute)
            .flatMap((attribute) =>
                attribute.options
                    .filter((option) => option.id !== subjectID && refersTo(option.links, subjectID, kind))
                    .map((option) => ({ id: option.id, name: option.name, kind: 'option' as const, ownerID: attribute.id }))
            )
    ];
    if (users.length === 0) return null;

    const open = (user: User) => {
        const owner = attributes.find((attribute) => attribute.id === user.ownerID);
        if (!owner) return;
        selectAttributes([owner]);
        if (user.kind === 'option') {
            const option = owner.options.find((candidate) => candidate.id === user.id);
            selectOptions(option ? [option] : null);
            setOptionFace('links');
        } else {
            selectOptions(null);
            setAttributeFace('links');
        }
        requestComposerReveal(user.id);
    };

    return (
        <span>
            <DropdownActions
                header={
                    <Chip type='button' aria-label={T._('Show what uses it', 'Admin')}>
                        {stringFormat(T._('Used by {0}', 'Admin'), users.length.toString())}
                    </Chip>
                }
                items={users.map((user) => (
                    <DropdownActionItem
                        key={user.id}
                        caption={user.kind === 'option' ? T._('Option', 'Admin') : T._('Attribute', 'Admin')}
                        onClick={() => open(user)}
                    >
                        {user.name}
                    </DropdownActionItem>
                ))}
                minWidth='220px'
                orientation='LEFT'
            />
        </span>
    );
};
